// release-home-view.guard.ts (functional guard)
import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot, Router, UrlTree } from '@angular/router';
import { filter, map, take } from 'rxjs';
import { ReleaseViewStateService } from '../../services/view-state/release-view-state.service';
import { AuthService } from '../services/auth/auth.service';

/**
 * Release home defaults to the manager "Everyone" flat list for Leads and Admins
 * (Bug 56800); testers and viewers keep the split "My view" page. Picking a view
 * from the toggle overrides the default for the rest of the session.
 *
 * This runs as a guard rather than a redirect inside the component so it also
 * covers deep links and back-navigation, and so it lands before ReleaseResolver
 * fetches the release (no double fetch, no flash of the wrong table).
 */
export const releaseHomeViewGuard = (route: ActivatedRouteSnapshot) => {
  const auth = inject(AuthService);
  const viewState = inject(ReleaseViewStateService);
  const router = inject(Router);

  return toObservable(auth.authCheckedSig).pipe(
    filter(Boolean), // the role is not known until the initial auth check settles
    take(1),
    map((): boolean | UrlTree => {
      if (!auth.isManagerSig()) return true;
      if (viewState.viewOverride() === 'mine') return true;

      const releaseId = route.paramMap.get('id');
      if (!releaseId) return true;
      return router.parseUrl(`/releases/${releaseId}/everyone`);
    }),
  );
};
